import React, { useEffect, useMemo, useRef, useState } from "react";
import { usePohrebka2 } from "../../context/Pohrebka2Context";

const CATEGORIES = [
  { id: "ohledaci_list", label: "Ohledací list", max: 5, hint: "Fotka nebo PDF ohledacího listu. Po uložení půjde použít pro výjezd." },
  { id: "prevzeti_zesnuleho", label: "Převzetí zesnulého", max: 5, hint: "Stav při převzetí zesnulého." },
  { id: "pred_obadem", label: "Před obřadem", max: 5, hint: "Zesnulý / rakev / příprava před obřadem." },
  { id: "kvetinova_vyzdoba", label: "Květinová výzdoba", max: 5, hint: "Výzdoba obřadu a květiny." },
  { id: "op_zesnuly", label: "OP zesnulého", max: 5, hint: "Občanský průkaz zesnulého." },
  { id: "op_objednavatel", label: "OP objednavatele", max: 5, hint: "Občanský průkaz objednavatele." },
  { id: "jine", label: "Jiné", max: 5, hint: "Ostatní fotky a dokumenty k zakázce." },
];

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 18,
  padding: 20,
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
};

const sectionStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  background: "#fafafa",
  padding: 16,
  marginBottom: 18,
};

const buttonStyle = {
  padding: "10px 14px",
  borderRadius: 10,
  border: "1px solid #d1d5db",
  background: "#fff",
  cursor: "pointer",
  fontWeight: 700,
};

const primaryBtnStyle = {
  ...buttonStyle,
  background: "#111827",
  color: "#fff",
  border: "1px solid #111827",
};

const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid #d1d5db",
  outline: "none",
  background: "#fff",
};

const thumbWrapStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 14,
  background: "#fff",
  overflow: "hidden",
};

const badgeStyle = {
  display: "inline-flex",
  alignItems: "center",
  borderRadius: 999,
  padding: "5px 10px",
  fontSize: 12,
  fontWeight: 800,
  background: "#eff6ff",
  color: "#1d4ed8",
  border: "1px solid #bfdbfe",
};

function safeStr(v) {
  return String(v || "").trim();
}

function formatDateTime(value, fallbackMs = 0) {
  const raw = safeStr(value);
  let d = null;

  if (raw) {
    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) d = parsed;
  }

  if (!d && Number.isFinite(Number(fallbackMs)) && Number(fallbackMs) > 0) {
    const parsed = new Date(Number(fallbackMs));
    if (!Number.isNaN(parsed.getTime())) d = parsed;
  }

  if (!d) return "Bez času";

  return d.toLocaleString("cs-CZ", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function isImageFile(item) {
  const mime = safeStr(item?.mime).toLowerCase();
  const file = safeStr(item?.file).toLowerCase();
  if (mime.startsWith("image/")) return true;
  return [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif"].some((ext) => file.endsWith(ext));
}

function categoryLabel(catId) {
  return CATEGORIES.find((x) => x.id === catId)?.label || catId || "Jiné";
}

export default function Pohrebka2FotkyForm() {
  const { order, currentOrderId, setOrder } = usePohrebka2();

  const orderId = useMemo(() => {
    return safeStr(currentOrderId || order?.id || order?.orderId || order?.zakazkaId || "");
  }, [currentOrderId, order]);

  const [selectedCategory, setSelectedCategory] = useState(() => safeStr(order?.fotky?.lastCategory) || "ohledaci_list");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadingCategory, setUploadingCategory] = useState("");
  const [deletingKey, setDeletingKey] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    setOrder((prev) => ({
      ...(prev || {}),
      fotky: {
        ...((prev && prev.fotky) || {}),
        lastCategory: selectedCategory,
      },
    }));
  }, [selectedCategory, setOrder]);

  const loadFiles = async () => {
    if (!orderId) {
      setFiles([]);
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const res = await fetch(`/api/foto/list?orderId=${encodeURIComponent(orderId)}`);
      const data = await res.json();

      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Nepodařilo se načíst fotky");
      }

      const rows = Array.isArray(data?.files) ? data.files : [];
      rows.sort((a, b) => {
        const ta = Number(a?.uploadedAtMs || a?.fileMtimeMs || 0) || 0;
        const tb = Number(b?.uploadedAtMs || b?.fileMtimeMs || 0) || 0;
        return tb - ta;
      });
      setFiles(rows);
    } catch (e) {
      setErrorMsg(String(e?.message || e));
      setFiles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles().catch(() => {});
  }, [orderId]);

  const grouped = useMemo(() => {
    const map = Object.fromEntries(CATEGORIES.map((c) => [c.id, []]));
    for (const row of Array.isArray(files) ? files : []) {
      const cat = safeStr(row?.kategorie) || "jine";
      if (!map[cat]) map[cat] = [];
      map[cat].push(row);
    }
    return map;
  }, [files]);

  const summary = useMemo(() => {
    const total = (Array.isArray(files) ? files : []).length;
    const ohledaciListCount = (grouped["ohledaci_list"] || []).length;
    const opZesnulyCount = (grouped["op_zesnuly"] || []).length;
    const opObjednavatelCount = (grouped["op_objednavatel"] || []).length;

    return {
      total,
      ohledaciListCount,
      opZesnulyCount,
      opObjednavatelCount,
      hasOhledaciList: ohledaciListCount > 0,
      hasOpZesnuly: opZesnulyCount > 0,
      hasOpObjednavatel: opObjednavatelCount > 0,
      missingImportant: [
        ohledaciListCount > 0 ? null : "Chybí ohledací list",
        opZesnulyCount > 0 ? null : "Chybí OP zesnulého",
        opObjednavatelCount > 0 ? null : "Chybí OP objednavatele",
      ].filter(Boolean),
    };
  }, [files, grouped]);

  const selectedCount = (grouped[selectedCategory] || []).length;
  const selectedMeta = CATEGORIES.find((x) => x.id === selectedCategory) || CATEGORIES[0];

  const handleUpload = async (ev) => {
    const picked = Array.from(ev.target.files || []);
    if (!picked.length) return;

    setStatusMsg("");
    setErrorMsg("");

    const currentCount = (grouped[selectedCategory] || []).length;
    const max = Number(selectedMeta?.max || 5) || 5;

    if (currentCount + picked.length > max) {
      setErrorMsg(`Kategorie ${selectedMeta.label} dovoluje maximálně ${max} souborů. Už tam je ${currentCount}.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    if (!orderId) {
      setErrorMsg("Nejdřív ulož zakázku, aby měla ID.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploadingCategory(selectedCategory);

    try {
      const fd = new FormData();
      fd.append("orderId", orderId);
      fd.append("kategorie", selectedCategory);

      for (const file of picked) {
        fd.append("files", file);
      }

      const res = await fetch("/api/foto/upload", {
        method: "POST",
        body: fd,
      });

      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Upload selhal");
      }

      setStatusMsg(`Nahráno: ${picked.length} soubor(ů) do kategorie ${selectedMeta.label}.`);
      if (fileInputRef.current) fileInputRef.current.value = "";
      await loadFiles();
    } catch (e) {
      setErrorMsg(String(e?.message || e));
    } finally {
      setUploadingCategory("");
    }
  };

  const handleDelete = async (item) => {
    const file = safeStr(item?.file);
    const kategorie = safeStr(item?.kategorie);

    if (!orderId || !file) return;
    if (!window.confirm(`Opravdu smazat soubor ${file}?`)) return;

    setDeletingKey(`${kategorie}/${file}`);
    setStatusMsg("");
    setErrorMsg("");

    try {
      const qs = new URLSearchParams();
      qs.set("orderId", orderId);
      qs.set("kategorie", kategorie);
      qs.set("file", file);

      const res = await fetch(`/api/foto/delete?${qs.toString()}`, {
        method: "DELETE",
      });

      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Smazání selhalo");
      }

      setStatusMsg(`Smazáno: ${file}`);
      await loadFiles();
    } catch (e) {
      setErrorMsg(String(e?.message || e));
    } finally {
      setDeletingKey("");
    }
  };

  const handleUseForVyjezd = async (item) => {
    if (!orderId) return;

    setStatusMsg("");
    setErrorMsg("");

    try {
      const payload = {
        orderId,
        file: safeStr(item?.file),
        kategorie: safeStr(item?.kategorie),
        meta: {
          takenAt: item?.takenAt || item?.uploadedAt || null,
          geo: item?.gps || null,
          address: item?.address || null,
        },
      };

      const res = await fetch("/api/vyjezd/foto-location", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || "Nepodařilo se uložit vazbu na výjezd");
      }

      setStatusMsg("Ohledací list byl uložen jako zdroj pro výjezd.");
    } catch (e) {
      setErrorMsg(String(e?.message || e));
    }
  };

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 30, fontWeight: 900, color: "#111827", marginBottom: 8 }}>
        Fotky zakázky
      </div>

      <div style={{ color: "#6b7280", fontSize: 15, marginBottom: 20 }}>
        Archiv fotek a dokumentů přímo k této zakázce. iPhone fotky jsou v pohodě, backend si drží čas, GPS i adresu, pokud je k dispozici.
      </div>

      <div style={{ ...sectionStyle, background: "#f8fafc" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
            Rychlý přehled zakázky
          </div>
          <div style={badgeStyle}>
            Celkem souborů: {summary.total}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <span style={{
            ...badgeStyle,
            background: summary.hasOhledaciList ? "#f0fdf4" : "#fef2f2",
            color: summary.hasOhledaciList ? "#166534" : "#991b1b",
            border: summary.hasOhledaciList ? "1px solid #86efac" : "1px solid #fecaca",
          }}>
            {summary.hasOhledaciList ? "✓ Ohledací list" : "✕ Ohledací list"}
          </span>

          <span style={{
            ...badgeStyle,
            background: summary.hasOpZesnuly ? "#f0fdf4" : "#fef2f2",
            color: summary.hasOpZesnuly ? "#166534" : "#991b1b",
            border: summary.hasOpZesnuly ? "1px solid #86efac" : "1px solid #fecaca",
          }}>
            {summary.hasOpZesnuly ? "✓ OP zesnulého" : "✕ OP zesnulého"}
          </span>

          <span style={{
            ...badgeStyle,
            background: summary.hasOpObjednavatel ? "#f0fdf4" : "#fef2f2",
            color: summary.hasOpObjednavatel ? "#166534" : "#991b1b",
            border: summary.hasOpObjednavatel ? "1px solid #86efac" : "1px solid #fecaca",
          }}>
            {summary.hasOpObjednavatel ? "✓ OP objednavatele" : "✕ OP objednavatele"}
          </span>
        </div>

        {summary.missingImportant.length ? (
          <div style={{ fontSize: 13, fontWeight: 700, color: "#92400e" }}>
            Chybí k doplnění: {summary.missingImportant.join(" • ")}
          </div>
        ) : (
          <div style={{ fontSize: 13, fontWeight: 700, color: "#166534" }}>
            Všechny hlavní dokumenty jsou nahrané.
          </div>
        )}
      </div>

      {!orderId ? (
        <div style={{ ...sectionStyle, background: "#fff7ed", border: "1px solid #fdba74", color: "#9a3412" }}>
          Tato zakázka ještě nemá ID. Nejdřív ji ulož a potom půjde nahrávat fotky.
        </div>
      ) : null}

      {statusMsg ? (
        <div style={{ ...sectionStyle, background: "#f0fdf4", border: "1px solid #86efac", color: "#166534" }}>
          {statusMsg}
        </div>
      ) : null}

      {errorMsg ? (
        <div style={{ ...sectionStyle, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b" }}>
          {errorMsg}
        </div>
      ) : null}

      <div style={sectionStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: "#111827", marginBottom: 6 }}>
              Nahrání souborů
            </div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              Vyber kategorii, nahraj až 5 souborů a vše se uloží přímo do zakázky.
            </div>
          </div>
          <div style={badgeStyle}>
            ID zakázky: {orderId || "—"}
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontWeight: 700, marginBottom: 6 }}>Kategorie</label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              style={inputStyle}
            >
              {CATEGORIES.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.label} ({(grouped[cat.id] || []).length}/{cat.max})
                </option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              {selectedMeta?.hint || ""}
            </div>
          </div>

          <div>
            <label style={{ display: "block", fontWeight: 700, marginBottom: 6 }}>
              Soubor(y)
            </label>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.heic,.heif"
              onChange={handleUpload}
              style={inputStyle}
              disabled={!orderId || !!uploadingCategory}
            />
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
              Aktuálně v kategorii: {selectedCount} / {selectedMeta?.max || 5}
            </div>
          </div>
        </div>

        {uploadingCategory ? (
          <div style={{ marginTop: 12, fontSize: 13, color: "#1d4ed8", fontWeight: 700 }}>
            Nahrávám do kategorie {categoryLabel(uploadingCategory)}…
          </div>
        ) : null}
      </div>

      <div style={sectionStyle}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#111827" }}>
            Archiv fotek a dokumentů
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={() => loadFiles()} style={buttonStyle}>
              Obnovit seznam
            </button>
            {orderId ? (
              <a
                href={`/api/foto/zip?orderId=${encodeURIComponent(orderId)}`}
                target="_blank"
                rel="noreferrer"
                style={{ ...buttonStyle, textDecoration: "none", display: "inline-flex", alignItems: "center" }}
              >
                Stáhnout ZIP
              </a>
            ) : null}
          </div>
        </div>

        {loading ? (
          <div style={{ color: "#6b7280" }}>Načítám fotky…</div>
        ) : null}

        {!loading && files.length === 0 ? (
          <div style={{ color: "#6b7280" }}>Zatím tu nejsou žádné fotky ani dokumenty.</div>
        ) : null}

        {!loading && files.length > 0 ? (
          <div style={{ display: "grid", gap: 18 }}>
            {CATEGORIES.map((cat) => {
              const rows = grouped[cat.id] || [];
              if (!rows.length) return null;

              return (
                <div key={cat.id}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 16, fontWeight: 800, color: "#111827" }}>
                      {cat.label}
                    </div>
                    <div style={badgeStyle}>
                      {rows.length} / {cat.max}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
                    {rows.map((item) => {
                      const key = `${item?.kategorie || ""}/${item?.file || ""}`;
                      const openUrl = item?.url || "#";
                      const img = isImageFile(item);
                      const timeText = formatDateTime(item?.takenAt || item?.uploadedAt, item?.uploadedAtMs || item?.fileMtimeMs || 0);

                      return (
                        <div key={key} style={thumbWrapStyle}>
                          <div style={{ height: 190, background: "#f8fafc", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            {img ? (
                              <a href={openUrl} target="_blank" rel="noreferrer" style={{ display: "block", width: "100%", height: "100%" }}>
                                <img
                                  src={openUrl}
                                  alt={safeStr(item?.file)}
                                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                                  loading="lazy"
                                />
                              </a>
                            ) : (
                              <a
                                href={openUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ color: "#1d4ed8", fontWeight: 800, textDecoration: "none", textAlign: "center", padding: 16 }}
                              >
                                Otevřít dokument
                                <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                                  {safeStr(item?.file)}
                                </div>
                              </a>
                            )}
                          </div>

                          <div style={{ padding: 12 }}>
                            <div style={{ fontWeight: 800, color: "#111827", marginBottom: 6, wordBreak: "break-word" }}>
                              {safeStr(item?.file) || "Soubor"}
                            </div>

                            <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 6 }}>
                              {timeText}
                            </div>

                            <div style={{ fontSize: 12, color: "#6b7280", minHeight: 34, marginBottom: 10 }}>
                              {safeStr(item?.address) || "Bez adresy / GPS"}
                            </div>

                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                              <a
                                href={openUrl}
                                target="_blank"
                                rel="noreferrer"
                                style={{ ...buttonStyle, textDecoration: "none", display: "inline-flex", alignItems: "center" }}
                              >
                                Otevřít
                              </a>

                              {cat.id === "ohledaci_list" ? (
                                <button
                                  type="button"
                                  onClick={() => handleUseForVyjezd(item)}
                                  style={primaryBtnStyle}
                                >
                                  Použít pro výjezd
                                </button>
                              ) : null}

                              <button
                                type="button"
                                onClick={() => handleDelete(item)}
                                style={{ ...buttonStyle, color: "#991b1b", border: "1px solid #fecaca", background: "#fff1f2" }}
                                disabled={deletingKey === key}
                              >
                                {deletingKey === key ? "Mažu…" : "Smazat"}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
